import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, UserSquare2, Users } from 'lucide-react';
import { participantService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type {
  Participant,
  ParticipantFilterOptions,
  ParticipantResponse,
} from '../types/participant';
import { unformatDocument } from '../types/api';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import ParticipantList from '../components/participants/ParticipantList';
import ParticipantForm from '../components/participants/ParticipantForm';
import ParticipantFilters from '../components/participants/ParticipantFilters';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/ui/LoadingSpinner';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

const DEFAULT_LIMIT = 10;

function Participants() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);
  const [filters, setFilters] = useState<ParticipantFilterOptions>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: participantsResponse, isLoading } = useQuery<ParticipantResponse>({
    queryKey: ['participants', filters],
    queryFn: () => participantService.getParticipants(filters),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Participant>) => participantService.createParticipant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
      messageHandler.success('Participante criado com sucesso!');
      closeModal();
    },
    onError: (error: any) => {
      messageHandler.error(error.message || 'Erro ao criar participante');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Participant) => participantService.updateParticipant(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
      messageHandler.success('Participante atualizado com sucesso!');
      closeModal();
    },
    onError: (error: any) => {
      messageHandler.error(error.message || 'Erro ao atualizar participante');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => participantService.deleteParticipant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
      messageHandler.success('Participante excluído com sucesso!');
    },
    onError: (error: any) => {
      messageHandler.error(error.message || 'Erro ao excluir participante');
    },
  });

  // Handlers
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const data = {
      full_name: formData.get('full_name') as string,
      cpf: unformatDocument(formData.get('cpf') as string),
      address: formData.get('address') as string,
      city: formData.get('city') as string,
      postal_code: unformatDocument(formData.get('postal_code') as string),
      phone: unformatDocument(formData.get('phone') as string),
      email: formData.get('email') as string,
    };

    if (editingParticipant) {
      updateMutation.mutate({ ...editingParticipant, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (participant: Participant) => {
    messageHandler.confirm(
      'Excluir Participante',
      'Tem certeza que deseja excluir este participante?',
      () => deleteMutation.mutate(participant.id)
    );
  };

  const handleFilterChange = (newFilters: ParticipantFilterOptions) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: DEFAULT_LIMIT,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingParticipant(null);
  };

  const participants = participantsResponse?.data || [];
  const pagination = participantsResponse?.pagination;
  const totalRecords = pagination?.total || 0;
  const isFiltered = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof ParticipantFilterOptions]
  );

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Participantes"
          description="Gerencie os participantes das campanhas."
          actions={
            <Button
              onClick={() => {
                setEditingParticipant(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Participante
            </Button>
          }
        />

        <ParticipantFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />

        {/* Total Records Count */}
        <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Users className="h-4 w-4 mr-2" />
          <span>
            Total de registros: <strong>{totalRecords}</strong>
          </span>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : participants.length > 0 ? (
          <>
            <ParticipantList
              participants={participants}
              onEdit={participant => {
                setEditingParticipant(participant);
                setModal({ type: 'edit' });
              }}
              onDelete={handleDelete}
              isLoading={isLoading}
            />

            {pagination && pagination.last_page > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={<UserSquare2 className="h-12 w-12" />}
            title={isFiltered ? 'Nenhum participante encontrado' : 'Nenhum participante cadastrado'}
            description={
              isFiltered
                ? 'Tente ajustar os filtros para encontrar o que procura'
                : 'Cadastre seu primeiro participante para começar'
            }
            action={{
              label: 'Criar Participante',
              onClick: () => {
                setEditingParticipant(null);
                setModal({ type: 'create' });
              },
            }}
          />
        )}

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingParticipant ? 'Editar Participante' : 'Novo Participante'}
        >
          <ParticipantForm
            participant={editingParticipant}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Participants;
